import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { format } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ITraining } from '../../../types/interfaces/training';
import { useStyles } from './AppTrainingLoadBarChart.styles';

export interface IChartDataPoint {
  date: string;
  totalLoad: number;
  trainingTitle: string;
}

export interface IAppTrainingLoadBarChartProps {
  trainings: ITraining[];
  height?: number;
}

export default function AppTrainingLoadBarChart({
  trainings,
  height = 400,
}: IAppTrainingLoadBarChartProps) {
  const theme = useTheme();

  const { classes } = useStyles();
  const { t } = useTranslation();

  const chartData = useMemo<IChartDataPoint[]>(() => {
    if (!trainings || trainings.length === 0) {
      return [];
    }

    const sortedTrainings = [...trainings]
      .filter((training) => training.startDate)
      .sort((a, b) => {
        if (!a.startDate || !b.startDate) return 0;
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateA.getTime() - dateB.getTime();
      });

    return sortedTrainings.map((training) => ({
      date: format(new Date(training.startDate!), 'dd.MM.yyyy'),
      totalLoad: training.totalLoad || 0,
      trainingTitle: training.title,
    }));
  }, [trainings]);

  if (chartData.length === 0) {
    return null;
  }

  const formatValue = (value: number) => {
    if (value === undefined || value === null) return '0';
    return Number(value.toFixed(2));
  };

  return (
    <Box className={classes.wrapper}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            label={{
              value: t('trainings.totalLoad') || 'Total Load',
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload as IChartDataPoint;
                return (
                  <Box
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      padding: theme.spacing(1.5),
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.spacing(1),
                    }}>
                    <Box sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                      {data.trainingTitle}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {t('trainings.trainingDate')}: {data.date}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', marginTop: 0.5 }}>
                      {t('trainings.totalLoad')}: {formatValue(data.totalLoad)}
                    </Box>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Bar
            dataKey="totalLoad"
            fill={theme.palette.primary.main}
            name={t('trainings.totalLoad') || 'Total Load'}
            radius={[4, 4, 0, 0]}
            activeBar={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
